/**
 * Auth Guard
 *
 * Wraps routes that require authentication.
 * Redirects to login if not authenticated.
 * Shows onboarding screen if user has no organization or workspace.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { OnboardingScreen } from '@semantical/components/onboarding/OnboardingScreen';

/**
 * Paths served anonymously by the public viewer family (scheme viewer + discover).
 * AuthGuard must not trigger an OIDC redirect on these even if it ends up
 * rendering them (e.g. due to outer Routes ranking quirks).
 */
function isPublicViewerPath(pathname: string): boolean {
    if (pathname === '/schemeViewer' || pathname.startsWith('/schemeViewer/')) return true;
    return /^\/vocabularies\/[^/]+\/(schemeViewer|discover)\/?$/.test(pathname);
}

function LoadingSpinner() {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                backgroundColor: '#1a1a2e',
                color: '#e0e0e0',
                fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
        >
            <div style={{ textAlign: 'center' }}>
                <div
                    style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #333',
                        borderTop: '3px solid #4a9eff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px',
                    }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p>Authenticating...</p>
            </div>
        </div>
    );
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
    const auth = useAuth();
    const location = useLocation();
    const onPublicViewerPath = isPublicViewerPath(location.pathname);
    const onLandingPath = location.pathname === '/';
    const { isAuthenticated, isLoading, login, organization, user } = auth;

    useEffect(() => {
        if (onPublicViewerPath || onLandingPath) return;
        if (!isLoading && !isAuthenticated) {
            login();
        }
    }, [isLoading, isAuthenticated, login, onPublicViewerPath, onLandingPath]);

    // Public viewer paths render their own page tree without any auth checks.
    if (onPublicViewerPath) {
        return <>{children}</>;
    }

    if (isLoading || !isAuthenticated) {
        // The landing path renders for anonymous visitors via HomeGate; we
        // must let children render rather than blocking with a spinner.
        if (onLandingPath) {
            return <>{children}</>;
        }
        return <LoadingSpinner />;
    }

    // No organization or no workspace — show onboarding
    if (!organization || !user?.workspaces?.length) {
        return <OnboardingScreen auth={auth} />;
    }

    return <>{children}</>;
}
